import { useEffect, useMemo, useState } from "react";
import { useRef } from "react";
import { QuestionCard, Header, Footer } from "./index";
import "./TestScreen.css";
import SectionTabs from "./SectionTabs";
import useAppHooks from "../../../../shared/hooks/useAppHooks";

import {
  saveAnswerRequest,
  submitExamRequest,
  updateQuestionStatus,
  fetchExamQuestionsListRequest,
  setPaperId,
  setActiveSection,
  fetchExamQuestionRequest,
  setCurrentQuestionIndex,
  resetPracticeStreak,
  hidePracticeProgressiveMessage,
} from "../../../../redux/exam/examSlice";
import { postAdaptiveSingleSaveRequest } from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import { useLocation } from "react-router-dom";
import SubmitModal from "../../../../shared/components/SubmitModal/SubmitModal";
import Modal from "../../../../shared/components/Modal/Modal";
import images from "../../../../utilities/images/images";
import { getQuestionStats } from "../utils/questionStats";
import { ClipLoader } from "react-spinners";
import { useSelector } from "react-redux";
import {
  getBackendQuestionStatus,
  getExamSaveAnswerFields,
  getQuestionIntegerAnswer,
  isIntegerTypeQuestion,
  normalizeExamQuestionStatus,
} from "../../../../shared/utilities/helpers/examQuestionStatus";
import { getExamSectionKey } from "../../../../shared/utilities/helpers/examSectionKey";
import PracticeModeProgressBar from "./PracticeModeProgressBar/PracticeModeProgressBar";
import { resolveImmediateReturnPath } from "../../../../custom_utilities/dashboardBannerNavigation";
import { clearExamResumeLocalState } from "../../../StudyDashboard/utils/resumeTestUtils";

const resolveTestReturnPath = (location, testDetails) => {
  return resolveImmediateReturnPath({
    location,
    source: testDetails?.source,
    testDetails,
  });
};

const TestScreen = () => {
  const { dispatch, history } = useAppHooks();
const location = useLocation();
  const {
    questionsData,
    questionsList,
    question,
    currentQuestionIndex,
    loading,
    questionBusy,
    testDetails,
    plannerDetails,
    paperId,
    selectedSetId,
    timeLeft,
    paper_duration,
    practiceStreak,
    instructions,
  } = useSelector((state) => state.examSlice);
  const adaptiveSingleSaveLoading = useSelector(
    (state) => state.adaptiveQBank?.loading?.singleSave === true
  );
  const stableQuestionRef = useRef({
    qid: null,
    index: 0,
  });

  const stats = getQuestionStats(questionsData);
  const isAdaptiveMode = location?.state?.useAdaptiveTestScreen === true;
  const adaptiveQuestionId =
    location?.state?.adaptive_question_id ??
    location?.state?.adaptiveQuestionId ??
    null;
  const adaptivePaperType = Number(location?.state?.paper_type ?? 20);
  const adaptiveFocusSetNo = Number(location?.state?.focusSetNo ?? 0) || undefined;
  const adaptiveIsResume = location?.state?.isResume === true;
  const adaptivePauseStorageKey = adaptiveQuestionId
    ? `adaptive_qbank_paused_focus_set_${adaptiveQuestionId}`
    : null;

  // const [activeQuestionId, setActiveQuestionId] = useState(null);
  const [isSubmitModalOpen, setSubmitModalOpen] = useState(false);
  const [showExitModal, setShowExitModal] = useState(false);
  const [showTimeUpModal, setShowTimeUpModal] = useState(false);
  const [pendingSubmitTime, setPendingSubmitTime] = useState(null);
  const autoSubmitTimeoutRef = useRef(null);
  const hasAutoSubmittedRef = useRef(false);
  const submitSaveSeenInFlightRef = useRef(false);
  const hasLoadedFreshAttemptRef = useRef(false);

  const isPracticeMode =
    !isAdaptiveMode && testDetails?.practiceMode === "practice";
  const allQuestionsAttempted =
    Array.isArray(questionsData) &&
    questionsData.length > 0 &&
    questionsData.every((q) => {
      const selected = q?.selectedOption;
      const hasSelectedOption = Array.isArray(selected)
        ? selected.length > 0
        : selected !== null &&
          selected !== undefined &&
          !(typeof selected === "string" && selected === "");

      const hasIntegerAnswer =
        q?.integerAnswer !== null &&
        q?.integerAnswer !== undefined &&
        String(q?.integerAnswer ?? "").trim() !== "";

      return hasSelectedOption || hasIntegerAnswer;
    });
  const isAdaptiveOnLastQuestion = useMemo(() => {
    if (!isAdaptiveMode) return false;
    if (!Array.isArray(questionsData) || questionsData.length === 0) return false;
    return currentQuestionIndex === questionsData.length - 1;
  }, [isAdaptiveMode, questionsData, currentQuestionIndex]);

  const adaptiveProgress = useMemo(() => {
    if (!isAdaptiveMode) {
      return { total: 0, completed: 0, percent: 0 };
    }

    const total = Array.isArray(questionsData) ? questionsData.length : 0;
    if (total === 0) {
      return { total: 0, completed: 0, percent: 0 };
    }

    let completed = questionsData.reduce((count, question) => {
      const status = normalizeExamQuestionStatus(question);
      const selected = question?.selectedOption;
      const hasSelectedOption = Array.isArray(selected)
        ? selected.length > 0
        : selected !== null &&
          selected !== undefined &&
          !(typeof selected === "string" && selected === "");

      const hasIntegerAnswer =
        question?.integerAnswer !== null &&
        question?.integerAnswer !== undefined &&
        String(question?.integerAnswer ?? "").trim() !== "";
      const hasSavedAttempt =
        Number(question?.is_saved) === 1 || Number(question?.question_status) > 0;

      const shouldCountProgress =
        hasSelectedOption ||
        hasIntegerAnswer ||
        hasSavedAttempt ||
        status === "answered" ||
        status === "unanswered" ||
        status === "marked" ||
        status === "answered_marked";

      return count + (shouldCountProgress ? 1 : 0);
    }, 0);

    // Resume fallback: some adaptive list payloads initially miss attempt fields.
    // Use locally stored resume snapshot so progress doesn't reset to 0 on first paint.
    if (completed === 0 && adaptiveIsResume && adaptiveQuestionId) {
      try {
        const rawSnapshot = localStorage.getItem(
          `exam_resume_state_${adaptiveQuestionId}`
        );
        if (rawSnapshot) {
          const parsed = JSON.parse(rawSnapshot);
          const snapshotQuestions = Array.isArray(parsed?.questions)
            ? parsed.questions
            : [];
          const snapshotAttempted = snapshotQuestions.reduce((acc, q) => {
            const selected = q?.selectedOption;
            const hasSelectedOption = Array.isArray(selected)
              ? selected.length > 0
              : selected !== null &&
                selected !== undefined &&
                !(typeof selected === "string" && selected === "");
            const hasIntegerAnswer =
              q?.integerAnswer !== null &&
              q?.integerAnswer !== undefined &&
              String(q?.integerAnswer ?? "").trim() !== "";
            const hasAttemptStatus = Number(q?.question_status) > 0;
            return acc + (hasSelectedOption || hasIntegerAnswer || hasAttemptStatus ? 1 : 0);
          }, 0);
          if (snapshotAttempted > 0) {
            completed = Math.min(snapshotAttempted, total);
          }
        }
      } catch (_) {
        // Ignore malformed snapshot; keep computed count.
      }
    }

    // Final resume guard: if backend opens directly on a later question
    // but attempt metadata arrives late, reflect at least reached index.
    if (adaptiveIsResume && Number.isInteger(currentQuestionIndex) && currentQuestionIndex > 0) {
      completed = Math.max(completed, currentQuestionIndex);
    }

    // If user answers current question during resume, reflect it immediately
    // instead of waiting for Save & Next navigation.
    if (adaptiveIsResume) {
      const currentQuestion = questionsData?.[currentQuestionIndex];
      const currentSelected = currentQuestion?.selectedOption;
      const currentHasSelectedOption = Array.isArray(currentSelected)
        ? currentSelected.length > 0
        : currentSelected !== null &&
          currentSelected !== undefined &&
          !(typeof currentSelected === "string" && currentSelected === "");
      const currentHasIntegerAnswer =
        currentQuestion?.integerAnswer !== null &&
        currentQuestion?.integerAnswer !== undefined &&
        String(currentQuestion?.integerAnswer ?? "").trim() !== "";

      if (
        (currentHasSelectedOption || currentHasIntegerAnswer) &&
        Number.isInteger(currentQuestionIndex) &&
        currentQuestionIndex >= 0
      ) {
        completed = Math.max(completed, currentQuestionIndex + 1);
      }
    }

    return {
      total,
      completed,
      percent: Math.min(100, Math.round((completed / total) * 100)),
    };
  }, [
    isAdaptiveMode,
    questionsData,
    adaptiveIsResume,
    adaptiveQuestionId,
    currentQuestionIndex,
  ]);
  const modalAttemptedCount = isAdaptiveMode
    ? adaptiveProgress.completed
    : stats.answered;
  const modalMarkedForReviewCount = stats.marked + stats.answeredMarked;
  const modalNotAttemptedCount = Math.max(
    (questionsData?.length || 0) -
      modalAttemptedCount -
      modalMarkedForReviewCount,
    0
  );
  // const resumeTestKey =
  //   testDetails?.testId || testDetails?.setId || testDetails?.paperId || null;
  const resumeTestKey =
  isAdaptiveMode
    ? adaptiveQuestionId
    : testDetails?.setId || testDetails?.testId || testDetails?.paperId;
  // ✅ reset sync flag when new test/resume key changes
useEffect(() => {
  hasSyncedAllRef.current = false;
  hasAlignedAdaptiveResumeRef.current = false;
  hasLoadedFreshAttemptRef.current = false;
}, [resumeTestKey]);

  // Ensure streak/progressive message state doesn't leak from a previous attempt.
  useEffect(() => {
    if (isPracticeMode && testDetails?.gamificationOn === true) {
      dispatch(resetPracticeStreak());
      dispatch(hidePracticeProgressiveMessage());
    }
  }, [dispatch, isPracticeMode, testDetails?.gamificationOn, resumeTestKey]);

  /* ======================= LOAD QUESTION LIST ======================= */
  // useEffect(() => {
  //   if (!paperId && testDetails?.paperId) {
  //     dispatch(setPaperId(testDetails?.paperId));
  //   }

  //   if (testDetails?.paperId) {
  //     dispatch(setCurrentQuestionIndex(0));

  //     dispatch(
  //       fetchExamQuestionsListRequest({
  //         paperId: testDetails.paperId,
  //         paperType: plannerDetails?.id,
  //         testId: testDetails?.testId,
  //         setId: selectedSetId,
  //       })
  //     );
  //   }
  // }, [dispatch, testDetails?.paperId]);
useEffect(() => {
  if (isAdaptiveMode && adaptiveQuestionId) {
    const shouldForceFreshStart = testDetails?.forceFreshStart === true;

    // Only Start / Reattempt wipes local progress. A mid-attempt refresh
    // keeps isResume:false but must still restore the in-progress snapshot.
    if (shouldForceFreshStart) {
      clearExamResumeLocalState(adaptiveQuestionId);
      hasLoadedFreshAttemptRef.current = true;
    } else if (hasLoadedFreshAttemptRef.current) {
      // forceFreshStart was just cleared after the first fetch — do not refetch.
      hasLoadedFreshAttemptRef.current = false;
      return;
    }

    let hasLocalResumeSnapshot = false;
    if (!shouldForceFreshStart) {
      try {
        const rawSnapshot = localStorage.getItem(
          `exam_resume_state_${adaptiveQuestionId}`
        );
        if (rawSnapshot) {
          const parsed = JSON.parse(rawSnapshot);
          hasLocalResumeSnapshot =
            parsed?.snapshotVersion === 2 &&
            String(parsed?.testKey) === String(adaptiveQuestionId);
        }
      } catch (_) {
        hasLocalResumeSnapshot = false;
      }
    }

    dispatch(
      fetchExamQuestionsListRequest({
        paperType: adaptivePaperType,
        adaptive_question_id: adaptiveQuestionId,
        focus_set_no: adaptiveFocusSetNo,
        practiceMode: testDetails?.practiceMode,
        gamificationOn: testDetails?.gamificationOn,
        isResume:
          !shouldForceFreshStart &&
          (adaptiveIsResume || hasLocalResumeSnapshot),
        forceFreshStart: shouldForceFreshStart,
      })
    );
    return;
  }

  const navState = location.state || {};

  const finalPaperId =
    testDetails?.paperId ?? navState?.paperId ?? null;

  const finalSetId =
    testDetails?.setId ?? navState?.setId ?? null;

  const finalTestId =
    testDetails?.testId ?? navState?.testId ?? null;

  const finalPaperType =
    testDetails?.paperType ?? navState?.paperType ?? plannerDetails?.id;

  if (!finalPaperId && !finalSetId) return;
  if (finalPaperType == null || finalPaperType === "") return;

  const resumeKey = finalSetId || finalTestId || finalPaperId;
  const shouldForceFreshStart = testDetails?.forceFreshStart === true;

  // Reattempt / Start wipes leftover snapshots. First-time start keeps
  // isResume:false, so a refresh must not treat that as a wipe.
  if (shouldForceFreshStart) {
    clearExamResumeLocalState(finalSetId, finalTestId, finalPaperId, resumeKey);
    dispatch(setCurrentQuestionIndex(0));
    hasLoadedFreshAttemptRef.current = true;
  } else if (hasLoadedFreshAttemptRef.current) {
    // forceFreshStart was just cleared after the first fetch — do not refetch.
    hasLoadedFreshAttemptRef.current = false;
    return;
  }

  let hasLocalResumeSnapshot = false;
  if (!shouldForceFreshStart) {
    try {
      const rawSnapshot = localStorage.getItem(`exam_resume_state_${resumeKey}`);
      if (rawSnapshot) {
        const parsed = JSON.parse(rawSnapshot);
        hasLocalResumeSnapshot =
          parsed?.snapshotVersion === 2 &&
          String(parsed?.testKey) === String(resumeKey);
      }
    } catch (_) {
      hasLocalResumeSnapshot = false;
    }
  }

  dispatch(
    fetchExamQuestionsListRequest({
      paperId: finalPaperId,
      paperType: finalPaperType,
      testId: finalTestId,
      setId: finalSetId,
      practiceMode: testDetails?.practiceMode,
      gamificationOn: testDetails?.gamificationOn,
      isResume:
        !shouldForceFreshStart &&
        (testDetails?.isResume === true || hasLocalResumeSnapshot),
      forceFreshStart: shouldForceFreshStart,
    })
  );

  // ✅ run only when test identity changes
}, [
  dispatch,
  testDetails?.paperId,
  testDetails?.setId,
  testDetails?.testId,
  testDetails?.paperType,
  testDetails?.isResume,
  testDetails?.practiceMode,
  testDetails?.gamificationOn,
  testDetails?.forceFreshStart,
  plannerDetails?.id,
  isAdaptiveMode,
  adaptiveQuestionId,
  adaptivePaperType,
  adaptiveFocusSetNo,
  adaptiveIsResume,
]);

  useEffect(() => {
    if (!resumeTestKey || !questionsData?.length) return;
    // Don't persist prior-attempt progress while starting a fresh run.
    if (testDetails?.forceFreshStart === true) return;

    const instructionData = instructions?.instruction || instructions || {};
    const isPractice =
      testDetails?.practiceMode === "practice" || location?.state?.practiceMode === "practice";

    const snapshot = {
      testKey: resumeTestKey,
      snapshotVersion: 2,
      updatedAt: Date.now(),
      currentQuestionQid: questionsData[currentQuestionIndex]?.qid ?? null,
      meta: {
        title:
          testDetails?.test_name ||
          testDetails?.paper_name ||
          instructionData?.paper_name ||
          questionsList?.paper_name ||
          "In-progress Test",
        subtitle:
          testDetails?.setId != null
            ? instructionData?.paper_code || instructionData?.set_name || "Practice"
            : instructionData?.paper_code || testDetails?.source || "Test",
        paperId: testDetails?.paperId ?? paperId ?? null,
        testId: testDetails?.testId ?? null,
        setId: testDetails?.setId ?? selectedSetId ?? null,
        paperType: testDetails?.paperType ?? plannerDetails?.id ?? null,
        practiceMode: isPractice ? "practice" : testDetails?.practiceMode || "test",
        plannerId: testDetails?.plannerId ?? plannerDetails?.planner_id ?? null,
        plannerTypeId:
          testDetails?.plannerTypeId ?? plannerDetails?.planner_type_id ?? null,
        categoryId: testDetails?.categoryId ?? plannerDetails?.category_id ?? "",
        source: testDetails?.source ?? null,
        gamificationOn: testDetails?.gamificationOn === true,
      },
      questions: questionsData.map((q) => ({
        qid: q.qid,
        status: q.status,
        question_status: getBackendQuestionStatus(q),
        selectedOption: q.selectedOption ?? null,
        integerAnswer: q.integerAnswer ?? q.integer_Answer ?? "",
        marked: q.marked === true,
      })),
    };

    localStorage.setItem(
      `exam_resume_state_${resumeTestKey}`,
      JSON.stringify(snapshot)
    );
  }, [
    resumeTestKey,
    questionsData,
    currentQuestionIndex,
    testDetails?.forceFreshStart,
  ]);


  // Track total time for analytics (practice mode)
useEffect(() => {
  if (!resumeTestKey) return;

  const interval = setInterval(() => {
    const prevTime = Number(
      localStorage.getItem(`exam_timeSpent_${resumeTestKey}`) || 0
    );

    localStorage.setItem(
      `exam_timeSpent_${resumeTestKey}`,
      prevTime + 1
    );
  }, 1000);

  return () => clearInterval(interval);
}, [resumeTestKey]);
  /* ======================= ACTIVE QUESTION ======================= */
  // useEffect(() => {
  //   if (
  //     questionsData &&
  //     questionsData[currentQuestionIndex] &&
  //     questionsData[currentQuestionIndex].qid
  //   ) {
  //     setActiveQuestionId(questionsData[currentQuestionIndex].qid);
  //   }
  // }, [questionsData, currentQuestionIndex]);

//   useEffect(() => {
//   if (
//     questionsData?.length &&
//     questionsData[currentQuestionIndex]?.qid
//   ) {
//     setActiveQuestionId(
//       questionsData[currentQuestionIndex].qid
//     );
//   }
// }, [questionsData, currentQuestionIndex]);

  /* ======================= SAVE & NEXT ======================= */
  const handleSaveAndNext = (e) => {
    e.preventDefault();

    const currentQuestion = questionsData[currentQuestionIndex];
    if (!currentQuestion) return;
const answerType = Number(currentQuestion.answer_type_id);

    if (isAdaptiveMode) {
      dispatch(
        postAdaptiveSingleSaveRequest(
          buildAdaptiveSingleSavePayload(currentQuestion, answerType)
        )
      );
    } else {

    dispatch(
      saveAnswerRequest({
        qid: currentQuestion.qid,
        paper_id: currentQuestion.paper_id,
        paper_type: testDetails?.paperType,
        test_id: testDetails?.testId,
        set_id: selectedSetId,
        time: currentQuestion?.time_taken ?? 0,
        ...getExamSaveAnswerFields(currentQuestion),
        question_status: getBackendQuestionStatus(currentQuestion),
      })
    );
    }

    if (currentQuestionIndex < questionsData.length - 1) {
      const nextIndex = currentQuestionIndex + 1;
      const nextQuestion = questionsData[nextIndex];

      const nextSection = getExamSectionKey(nextQuestion);

      const currSection = getExamSectionKey(currentQuestion);

      if (nextSection !== currSection) {
        dispatch(setActiveSection(nextSection));
      }

      dispatch(
        fetchExamQuestionRequest({
          qid: nextQuestion.qid,
          paperId: nextQuestion.paper_id,
          paperType: isAdaptiveMode ? adaptivePaperType : undefined,
          adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
          focus_set_no: isAdaptiveMode ? adaptiveFocusSetNo : undefined,
          testId: testDetails?.testId,
          setId: selectedSetId,
        })
      );
    }
  };

  const handleSelectQuestion = (qid) => {
    // setActiveQuestionId(qid);

    const selectedQuestion = questionsData.find(
      (q) => String(q.qid) === String(qid)
    );
    if (!selectedQuestion) return;

    const selectedIndex = questionsData.findIndex(
      (q) => String(q.qid) === String(qid)
    );

    dispatch(
      updateQuestionStatus({
        qid: selectedQuestion.qid,
        makeCurrent: true,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );

    if (selectedIndex >= 0) {
      dispatch(setCurrentQuestionIndex(selectedIndex));
      dispatch(setActiveSection(getExamSectionKey(selectedQuestion)));
    }

    dispatch(
      fetchExamQuestionRequest({
        qid: selectedQuestion.qid,
        paperId: selectedQuestion.paper_id,
        paperType: isAdaptiveMode ? adaptivePaperType : undefined,
        adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
        focus_set_no: isAdaptiveMode ? adaptiveFocusSetNo : undefined,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );
  };

  const onMarkReview = () => {
    const currentQuestion = questionsData[currentQuestionIndex];
    if (!currentQuestion) return;

    dispatch(
      updateQuestionStatus({
        qid: currentQuestion.qid,
        markReview: true,
        testId: testDetails?.testId,
        setId: selectedSetId,
      })
    );
  };

  /* ======================= SUBMIT TEST ======================= */
const finalizeSubmitExam = (finalTime) => {
  const currentQuestion = questionsData?.[currentQuestionIndex];

  if (isAdaptiveMode && adaptivePauseStorageKey) {
    localStorage.removeItem(adaptivePauseStorageKey);
  }

  const resumeTestKey =
    testDetails?.setId || testDetails?.testId || testDetails?.paperId;

  localStorage.setItem(
    `exam_finalTime_${resumeTestKey}`,
    finalTime
  );

  // Drop local resume/answers asap so a reattempt never restores them.
  clearExamResumeLocalState(
    testDetails?.adaptive_question_id,
    testDetails?.setId,
    testDetails?.testId,
    testDetails?.paperId,
    selectedSetId,
    paperId,
    resumeTestKey,
    isAdaptiveMode ? adaptiveQuestionId : null
  );

  if (process.env.NODE_ENV !== "production") {
    console.log("FINAL TIMER:", finalTime);
  }

  dispatch(
    submitExamRequest({
      paperType: isAdaptiveMode ? adaptivePaperType : testDetails?.paperType,
      paperId,
      plannerId: plannerDetails?.planner_id,
      setId: selectedSetId,
      time: finalTime,
      adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
    })
  );

  if (isPracticeMode) {
    dispatch(resetPracticeStreak());
  }

  setSubmitModalOpen(false);
  setShowTimeUpModal(false);

  if (isAdaptiveMode) {
    history.push("/practice/adaptive-question-bank/submit", {
      adaptive_question_id: adaptiveQuestionId,
      paper_type: adaptivePaperType,
      topic: location?.state?.topic,
      topic_id: location?.state?.topic_id ?? location?.state?.topic?.topic_id,
      focusSetNo: location?.state?.focusSetNo ?? 1,
      summarySelectedTopics: location?.state?.summarySelectedTopics ?? [],
      isTopicCustomizationApplied:
        location?.state?.isTopicCustomizationApplied === true,
    });
  } else if (isPracticeMode) {
    const returnPath = resolveTestReturnPath(location, testDetails);
    history.push("/practice-summary", returnPath ? { returnTo: returnPath } : undefined);
  } else {
    const returnPath = resolveTestReturnPath(location, testDetails);
    history.push("/analytics", {
      from_submit: true,
      paper_type: testDetails?.paperType ?? plannerDetails?.id,
      paper_id:
        testDetails?.source === "yearwise"
          ? testDetails?.testId
          : paperId ?? testDetails?.paperId,
      set_id: selectedSetId ?? testDetails?.setId,
      planner_test_id:
        testDetails?.planner_test_id ?? plannerDetails?.planner_id,
      ...(returnPath ? { returnTo: returnPath } : {}),
    });
  }
};

const handleSubmitExam = () => {
  if (hasAutoSubmittedRef.current && showTimeUpModal) return;
  if (pendingSubmitTime !== null) return;

  if (autoSubmitTimeoutRef.current) {
    clearTimeout(autoSubmitTimeoutRef.current);
    autoSubmitTimeoutRef.current = null;
  }

  const finalTime = timeLeft; // ✅ EXACT UI TIMER
  const currentQuestion = questionsData?.[currentQuestionIndex];
  const currentAnswerType = Number(currentQuestion?.answer_type_id);

  // Persist current response before navigating away (major test + adaptive).
  // Must wait for save to finish — otherwise submit can race ahead and the
  // current question (often the last one) is counted as unattempted.
  if (currentQuestion?.qid) {
    submitSaveSeenInFlightRef.current = false;
    dispatch(
      isAdaptiveMode
        ? postAdaptiveSingleSaveRequest(
            buildAdaptiveSingleSavePayload(currentQuestion, currentAnswerType)
          )
        : saveAnswerRequest({
            paper_type: testDetails?.paperType ?? testDetails?.paper_type,
            paper_id: currentQuestion.paper_id ?? paperId,
            qid: currentQuestion.qid,
            test_id: testDetails?.testId,
            set_id: selectedSetId ?? testDetails?.setId,
            time: currentQuestion?.time ?? currentQuestion?.time_taken ?? 0,
            triggerStreak: true,
            question_status: getBackendQuestionStatus(currentQuestion),
            ...getExamSaveAnswerFields(currentQuestion),
            // Client-only: keep UI on current Q and wait before exam_submit.
            skipAdvance: true,
            awaitSave: true,
          })
    );
    setPendingSubmitTime(finalTime);
    return;
  }

  finalizeSubmitExam(finalTime);
};

useEffect(() => {
  if (pendingSubmitTime === null) return;

  const saveStillInFlight = isAdaptiveMode
    ? adaptiveSingleSaveLoading
    : loading;

  if (saveStillInFlight) {
    submitSaveSeenInFlightRef.current = true;
    return;
  }

  // Don't finalize until we've observed the save actually start; otherwise a
  // render before loading flips true can submit too early and drop the answer.
  if (!submitSaveSeenInFlightRef.current) return;

  submitSaveSeenInFlightRef.current = false;
  finalizeSubmitExam(pendingSubmitTime);
  setPendingSubmitTime(null);
}, [pendingSubmitTime, loading, adaptiveSingleSaveLoading, isAdaptiveMode]);

const handleTimeUpAutoSubmit = () => {
  if (isPracticeMode || isAdaptiveMode || hasAutoSubmittedRef.current) return;

  hasAutoSubmittedRef.current = true;
  setShowTimeUpModal(true);

  autoSubmitTimeoutRef.current = setTimeout(() => {
    handleSubmitExam();
  }, 2200);
};

useEffect(() => {
  return () => {
    if (autoSubmitTimeoutRef.current) {
      clearTimeout(autoSubmitTimeoutRef.current);
    }
  };
}, []);
const handleExitTest = () => {
  const currentQuestion = questionsData[currentQuestionIndex];
  if (currentQuestion) {
    const answerType = Number(currentQuestion.answer_type_id);
    dispatch(
      isAdaptiveMode
        ? postAdaptiveSingleSaveRequest(
            buildAdaptiveSingleSavePayload(currentQuestion, answerType)
          )
        : saveAnswerRequest({
            qid: currentQuestion.qid,
            paper_id: currentQuestion.paper_id,
            paper_type: testDetails?.paperType,
            test_id: testDetails?.testId,
            set_id: selectedSetId,
            time: currentQuestion?.time_taken ?? 0,
            ...getExamSaveAnswerFields(currentQuestion),
            question_status: getBackendQuestionStatus(currentQuestion),
            skipAdvance: true,
          })
    );
  }

  const source = testDetails?.source;

  if (isAdaptiveMode) {
    if (adaptivePauseStorageKey) {
      const pausedFocusSetNo =
        Number(location?.state?.focusSetNo ?? location?.state?.pausedFocusSetNo ?? 1) || 1;
      localStorage.setItem(adaptivePauseStorageKey, String(pausedFocusSetNo));
    }
    history.push({
      pathname: "/practice/adaptive-question-bank/next-focus-set",
      state: {
        topic: location?.state?.topic,
        topic_id: location?.state?.topic_id,
        pausedFocusSetNo:
          location?.state?.focusSetNo ??
          location?.state?.pausedFocusSetNo ??
          1,
        isPaused: true,
      },
    });
    return;
  }

  const returnPath = resolveTestReturnPath(location, testDetails);
  if (returnPath) {
    history.push(returnPath);
    return;
  }

  if (source === "mistake_book_practice_set") {
    history.push("/practice/mistake-book/practice-sets");
  } else if (source === "topicwise") {
    history.push("/practice/topic-wise/pyqset");
  } else if (source === "yearwise") {
    history.push("/practice/paper-listing?type=Year Wise");
  } else {
    history.push("/practice/paper-listing");
  }
};

const activeQuestion =
  questionsData?.[currentQuestionIndex] || null;
const loadedQuestionQid = useMemo(() => {
  const payload = question;
  const normalized = Array.isArray(payload)
    ? payload[0]
    : Array.isArray(payload?.data)
      ? payload.data[0]
      : payload?.data || payload;
  return normalized?.qid ?? null;
}, [question]);
const adaptiveTopicName = isAdaptiveMode
  ? String(
      questionsList?.focus_set ??
        questionsList?.focusSet ??
        activeQuestion?.focus_set ??
        activeQuestion?.focusSet ??
        location?.state?.focus_set ??
        ""
    ).trim()
  : "";

function buildAdaptiveSingleSavePayload(question, answerType) {
  const selectedOptionRaw = question?.selectedOption;
  const optionId = Array.isArray(selectedOptionRaw)
    ? selectedOptionRaw
    : selectedOptionRaw
      ? [selectedOptionRaw]
      : [];

  return {
    option_id: optionId,
    qid: question?.qid,
    time: Number(question?.time_taken ?? 0),
    adaptive_question_id: adaptiveQuestionId,
    question_status: Number(getBackendQuestionStatus(question) ?? 0),
    paper_type: adaptivePaperType,
    option_int: isIntegerTypeQuestion(question)
      ? getQuestionIntegerAnswer(question)
      : "",
  };
}


// Universal safeguard: keep detailed question payload aligned with current list row.
// Handles switching between test types before resume.
// Do not block on `loading` — Save & Next advances the index while save is in
// flight; fetching the next question in parallel keeps one continuous overlay.
useEffect(() => {
  if (!Array.isArray(questionsData) || questionsData.length === 0) return;
  if (!activeQuestion?.qid) return;
  if (String(loadedQuestionQid ?? "") === String(activeQuestion.qid)) return;

  dispatch(
    fetchExamQuestionRequest({
      qid: activeQuestion.qid,
      paperId: activeQuestion.paper_id,
      paperType: isAdaptiveMode ? adaptivePaperType : undefined,
      adaptive_question_id: isAdaptiveMode ? adaptiveQuestionId : undefined,
      focus_set_no: isAdaptiveMode ? adaptiveFocusSetNo : undefined,
      testId: testDetails?.testId,
      setId: selectedSetId,
    })
  );
}, [
  dispatch,
  questionsData,
  activeQuestion?.qid,
  activeQuestion?.paper_id,
  loadedQuestionQid,
  isAdaptiveMode,
  adaptivePaperType,
  adaptiveFocusSetNo,
  adaptiveQuestionId,
  testDetails?.testId,
  selectedSetId,
]);

// Adaptive resume: force cursor to backend userGivenLastQuestionId when present.
// This avoids opening Q1 when backend pointer key differs across payloads.
useEffect(() => {
  if (!isAdaptiveMode || !adaptiveIsResume) return;
  if (hasAlignedAdaptiveResumeRef.current) return;
  if (!Array.isArray(questionsData) || questionsData.length === 0) return;

  const rawLastId = questionsList?.userGivenLastQuestionId;
  if (rawLastId === null || rawLastId === undefined || Number(rawLastId) === 0) return;

  const matchesLastId = (q) =>
    String(q?.qid) === String(rawLastId) ||
    String(q?.id) === String(rawLastId) ||
    String(q?.question_id) === String(rawLastId) ||
    String(q?.questionId) === String(rawLastId);

  const targetIndex = questionsData.findIndex(matchesLastId);
  if (targetIndex < 0 || targetIndex === currentQuestionIndex) {
    hasAlignedAdaptiveResumeRef.current = true;
    return;
  }

  const targetQuestion = questionsData[targetIndex];
  hasAlignedAdaptiveResumeRef.current = true;
  dispatch(setCurrentQuestionIndex(targetIndex));
  dispatch(setActiveSection(getExamSectionKey(targetQuestion)));
  dispatch(
    fetchExamQuestionRequest({
      qid: targetQuestion.qid,
      paperId: targetQuestion.paper_id,
      paperType: adaptivePaperType,
      adaptive_question_id: adaptiveQuestionId,
      focus_set_no: adaptiveFocusSetNo,
      testId: testDetails?.testId,
      setId: selectedSetId,
    })
  );
}, [
  dispatch,
  isAdaptiveMode,
  adaptiveIsResume,
  questionsData,
  questionsList?.userGivenLastQuestionId,
  currentQuestionIndex,
  adaptivePaperType,
  adaptiveQuestionId,
  adaptiveFocusSetNo,
  testDetails?.testId,
  selectedSetId,
]);

//Sync all answered questions ONCE (no multiple API calls)
const hasSyncedAllRef = useRef(false);
const hasAlignedAdaptiveResumeRef = useRef(false);

useEffect(() => {
  if (isAdaptiveMode) return;

  const shouldSyncAnswered = isAdaptiveMode
    ? adaptiveIsResume
    : testDetails?.isResume === true;
  if (
    shouldSyncAnswered &&
    questionsData?.length &&
    !hasSyncedAllRef.current
  ) {
    hasSyncedAllRef.current = true;

    const answeredQuestions = questionsData.filter((q) => {
      const hasSelectedOption =
        (Array.isArray(q.selectedOption) && q.selectedOption.length > 0) ||
        (q.selectedOption !== null &&
          q.selectedOption !== undefined &&
          q.selectedOption !== "");
      // Treat "0" as a valid integer answer (truthy checks drop it).
      const hasIntegerAnswer = getQuestionIntegerAnswer(q).trim() !== "";
      return hasSelectedOption || hasIntegerAnswer;
    });

    answeredQuestions.forEach((q, index) => {
      setTimeout(() => {
        const answerType = Number(q.answer_type_id);

        dispatch(
          isAdaptiveMode
            ? postAdaptiveSingleSaveRequest(
                buildAdaptiveSingleSavePayload(q, answerType)
              )
            : saveAnswerRequest({
                qid: q.qid,
                paper_id: q.paper_id,
                paper_type: testDetails?.paperType,
                test_id: testDetails?.testId,
                set_id: selectedSetId,
                time: q?.time_taken ?? 0,
                ...getExamSaveAnswerFields(q),
                question_status: getBackendQuestionStatus(q),
                skipAdvance: true,
              })
        );
      }, index * 30); // spacing prevents API burst
    });
  }
}, [adaptiveIsResume, isAdaptiveMode, questionsData, testDetails?.isResume]);




//QUES NOT SAVING AFTER RESUME- FIXED BELOW
//Below useeffect code fixed this issue

// useEffect(() => {
//   if (
//     testDetails?.isResume === true &&
//     questionsData?.length
//   ) {
//     questionsData.forEach((q) => {
//       const answerType = Number(q.answer_type_id);

//       const hasAnswer =
//         (Array.isArray(q.selectedOption) && q.selectedOption.length > 0) ||
//         (q.selectedOption && q.selectedOption !== "") ||
//         (q.integerAnswer && String(q.integerAnswer).trim() !== "");

//       if (hasAnswer) {
//         dispatch(
//           saveAnswerRequest({
//             qid: q.qid,
//             paper_id: q.paper_id,
//             paper_type: testDetails?.paperType,
//             test_id: testDetails?.testId,
//             set_id: selectedSetId,
//             time: q?.time_taken ?? 0,

//             option_id:
//               answerType === 1 || answerType === 2
//                 ? q.selectedOption ?? ""
//                 : "",

//             option_int:
//               answerType === 3
//                 ? q.integerAnswer ?? q.integer_Answer ?? ""
//                 : "",

//             question_status: getBackendQuestionStatus(q),
//             skipAdvance: true, 
//           })
//         );
//       }
//     });
//   }
// }, [testDetails?.isResume, questionsData]);

  // Full-page spinner only for the first load. Save & Next / question switches
  // keep the exam chrome mounted and use a soft overlay (avoids double flicker).
  const hasExamQuestions =
    Array.isArray(questionsData) && questionsData.length > 0;
  const isInitialLoading = loading && !hasExamQuestions;
  const isQuestionTransitioning =
    Boolean(activeQuestion?.qid) &&
    String(loadedQuestionQid ?? "") !== String(activeQuestion.qid);
  const showQuestionOverlay =
    hasExamQuestions && (loading || questionBusy || isQuestionTransitioning);

  // Keep last fully-loaded question visible under the overlay so stage height
  // (and footer position) don't collapse while the next question loads.
  if (
    activeQuestion?.qid != null &&
    !isQuestionTransitioning &&
    String(loadedQuestionQid ?? "") === String(activeQuestion.qid)
  ) {
    stableQuestionRef.current = {
      qid: activeQuestion.qid,
      index: currentQuestionIndex,
    };
  }
  const cardQuestionId = isQuestionTransitioning
    ? stableQuestionRef.current.qid ?? activeQuestion?.qid
    : activeQuestion?.qid;
  const cardQuestionIndex = isQuestionTransitioning
    ? stableQuestionRef.current.index
    : currentQuestionIndex;

  if (isInitialLoading) {
    return (
      <div className="loading_center_container">
        <ClipLoader size={40} color="#C61C26" />
      </div>
    );
  }

  return (
    <div className={`exam-wrapper${showQuestionOverlay ? " is-question-busy" : ""}`}>
      {isAdaptiveMode ? (
        <div className="header-container">
          <div className="header-left" onClick={() => setShowExitModal(true)}>
            <img src={images.new_backarrow} alt="Back" className="back-icon" />
          </div>
          <div className="header-center">
            <span className="adaptive-header-topic">{adaptiveTopicName}</span>
          </div>
          <div className="header-right">
            <button
              type="button"
              className="header-submit-btn"
              disabled={!isAdaptiveOnLastQuestion}
              title={
                isAdaptiveOnLastQuestion
                  ? "Submit your test"
                  : "Go to the last question to submit"
              }
              onClick={() => {
                if (!isAdaptiveOnLastQuestion) return;
                setSubmitModalOpen(true);
              }}
            >
              Submit
            </button>
          </div>
        </div>
      ) : (
        <Header
         onSubmit={() => setSubmitModalOpen(true)} 
          onExitClick={() => setShowExitModal(true)}
          onTimeUp={handleTimeUpAutoSubmit}
         />
      )}

      {/* <div className="exam-body"> */}
        {/* <div className="exam-content"> */}
   <div className={`exam-body ${isAdaptiveMode ? "" : "with-overview"}`}>
          <div className="exam_content_container">
            
          {isPracticeMode && testDetails?.gamificationOn === true && (
            <div style={{ margin: "0 0 24px 0", padding: "0 16px" }}>
              <PracticeModeProgressBar
                totalQuestions={questionsData.length}
                streak={practiceStreak}
                currentQuestionIndex={currentQuestionIndex}
                allQuestionsAttempted={allQuestionsAttempted}
              />
            </div>
          )}

            {!isAdaptiveMode && (
              <SectionTabs onSelectQuestion={handleSelectQuestion} />
            )}
            {isAdaptiveMode && (
              <div className="adaptive-test-progress-wrap">
                <div className="adaptive-test-progress-head">
                  <span>Progress</span>
                  <span>
                    {adaptiveProgress.completed}/{adaptiveProgress.total} Questions
                  </span>
                </div>
                <div className="adaptive-test-progress-track">
                  <div
                    className="adaptive-test-progress-fill"
                    style={{ width: `${adaptiveProgress.percent}%` }}
                  />
                </div>
              </div>
            )}

            {/* <QuestionCard questionId={activeQuestionId} /> */}
            {/* {activeQuestionId && (
  <QuestionCard questionId={activeQuestionId} />
)} */}

            <div className="exam-question-stage">
              {showQuestionOverlay && (
                <div className="exam-question-overlay" aria-busy="true">
                  <ClipLoader size={36} color="#C61C26" />
                </div>
              )}
              {cardQuestionId != null && cardQuestionId !== "" && (
                <QuestionCard
                  key={cardQuestionId + "-" + cardQuestionIndex}
                  questionId={cardQuestionId}
                />
              )}
            </div>

            <Footer
              onSaveNext={handleSaveAndNext}
              onMarkReview={onMarkReview}
              busy={showQuestionOverlay}
            />
          </div>
        </div>
      {/* </div> */}

      {/* ======================= SUBMIT MODAL ======================= */}
      <SubmitModal
        isOpen={isSubmitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        onSubmit={handleSubmitExam}
        title="Submit Exam?"
        subtitle="Are you sure you want to submit your answers?"
        imageSrc={images.submit_icon}
        totalQuestions={questionsData?.length || 0}
        notAttempted={modalNotAttemptedCount}
        attempted={modalAttemptedCount}
        markedForReview={modalMarkedForReviewCount}
        cancelButtonProps={{
          title: "Cancel",
          variant: "outlined",
          color: "error",
          onClick: () => setSubmitModalOpen(false),
        }}
        submitButtonProps={{
          title: loading ? "Submitting..." : "Submit",
          variant: "contained",
          color: "error",
          onClick: handleSubmitExam,
        }}
      />
      {/* This is for exit modal */}
      <SubmitModal
  isOpen={showExitModal}
  onClose={() => setShowExitModal(false)}
  title="Exit Test"
  subtitle="Are you sure you want to exit? Your answers are saved and you can resume anytime."
  imageSrc={images.exit_modal_icon}
  totalQuestions={questionsData?.length || 0}
  notAttempted={modalNotAttemptedCount}
  attempted={modalAttemptedCount}
  markedForReview={modalMarkedForReviewCount}
  cancelButtonProps={{
    title: "Go Back To Test",
    variant: "outlined",
    color: "error",
    onClick: () => setShowExitModal(false),
  }}
  submitButtonProps={{
    title: "Exit Test",
    variant: "contained",
    color: "error",
    onClick: handleExitTest,
  }}
/>

      <Modal
        isOpen={showTimeUpModal}
        onClose={() => {}}
        height="14rem"
        width="32rem"
      >
        <div className="timeup-modal-content">
          <div className="timeup-modal-title">Time is up</div>
          <div className="timeup-modal-subtitle">
            The test will be auto submitted.
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default TestScreen;
